import { createI18n } from 'vue-i18n'
import type { PrimeVueConfiguration } from 'primevue/config'
import it from './locales/it.json'
import en from './locales/en.json'
import { primevueIt } from './primevue/it'
import { primevueEn } from './primevue/en'

export const SUPPORTED_LOCALES = ['it', 'en'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

export const LOCALE_LABELS: Record<AppLocale, string> = {
  it: 'Italiano',
  en: 'English',
}

/**
 * Scelta ESPLICITA dell'utente su questo browser (LangSwitcher, preferenza personale).
 * Vince sempre sulla lingua che arriva dal server.
 */
const STORAGE_KEY = 'app-locale'

/**
 * Ultima lingua EFFETTIVA ricevuta dal server (personale -> sede -> tenant, risolta lato
 * backend). Tenuta in una chiave distinta di proposito: se la si scrivesse in `STORAGE_KEY`
 * diventerebbe indistinguibile da una scelta dell'utente, e da lì in poi nessun cambio di
 * lingua deciso dallo studio verrebbe più applicato.
 */
const SERVER_STORAGE_KEY = 'app-locale-server'

const DEFAULT_LOCALE: AppLocale = 'it'

function isSupported(value: string): value is AppLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

/**
 * Riduce un tag di lingua a un locale dell'app. Il server può mandare una variante
 * regionale ('en-GB'): la lingua dell'interfaccia è comunque 'en', mentre il formato di
 * date e numeri della variante è gestito altrove (`configureDateLocale` in `main.ts`).
 * Ritorna `null` per una lingua che l'app non traduce: meglio restare com'è che mostrare
 * mezza interfaccia nel fallback.
 */
export function resolveAppLocale(language: string | null | undefined): AppLocale | null {
  if (!language) return null
  const normalized = language.trim().toLowerCase()
  if (!normalized) return null
  if (isSupported(normalized)) return normalized
  const base = normalized.split('-')[0]
  return base && isSupported(base) ? base : null
}

function readStoredLocale(key: string): AppLocale | null {
  const stored = localStorage.getItem(key)
  return stored && isSupported(stored) ? stored : null
}

/** true se su questo browser l'utente ha già scelto una lingua a mano. */
export function hasExplicitLocale(): boolean {
  return readStoredLocale(STORAGE_KEY) !== null
}

function pickInitialLocale(): AppLocale {
  return readStoredLocale(STORAGE_KEY) ?? readStoredLocale(SERVER_STORAGE_KEY) ?? DEFAULT_LOCALE
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: pickInitialLocale(),
  fallbackLocale: 'en',
  messages: { it, en },
})

const PRIMEVUE_LOCALES: Record<AppLocale, object> = {
  it: primevueIt,
  en: primevueEn,
}

// Initial PrimeVue locale to pass to app.use(PrimeVue, { ... }) in main.ts.
export const initialPrimeVueLocale = PRIMEVUE_LOCALES[i18n.global.locale.value as AppLocale]

/**
 * Config di PrimeVue catturata all'installazione in `main.ts`.
 *
 * NON si usa `usePrimeVue()`: è un `inject()`, quindi funziona solo durante il `setup()`
 * sincrono di un componente e lancia `PrimeVue is not installed!` da qualunque altro punto.
 * Il cambio di lingua parte anche da posti che quel vincolo non lo rispettano — dopo l'`await`
 * di una fetch, in un then, in un handler asincrono — e lì l'eccezione veniva inghiottita dal
 * catch del chiamante: al login la pagina mostrava "Impossibile connettersi al server" con la
 * richiesta andata a buon fine con 200.
 */
let primeVueConfig: PrimeVueConfiguration | null = null

/** Chiamata una volta in `main.ts`, subito dopo `app.use(PrimeVue, ...)`. */
export function registerPrimeVueConfig(config: PrimeVueConfiguration): void {
  primeVueConfig = config
}

function applyLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale
  document.documentElement.setAttribute('lang', locale)

  // La config di PrimeVue è reattiva: si assegnano le chiavi una a una invece di sostituire
  // l'oggetto, così i watcher esistenti vedono il cambiamento.
  if (primeVueConfig) {
    Object.assign(primeVueConfig.locale ?? {}, PRIMEVUE_LOCALES[locale])
  }
}

// Switch both vue-i18n and PrimeVue at runtime, persist the choice.
export function setLocale(locale: AppLocale): void {
  localStorage.setItem(STORAGE_KEY, locale)
  applyLocale(locale)
}

/**
 * Dimentica la scelta esplicita: da qui in poi vale di nuovo la lingua del server. Serve
 * quando l'utente rimette la preferenza personale su "eredita".
 */
export function clearExplicitLocale(): void {
  localStorage.removeItem(STORAGE_KEY)
}

/**
 * Applica la lingua effettiva risolta dal server (login, SSO, attivazione, preferenza
 * personale riletta). Non tocca nulla se l'utente ha già scelto a mano su questo browser:
 * sovrascrivere quella scelta significherebbe che cambiare lingua col selettore e
 * ricaricare la pagina riporta indietro alla lingua di prima.
 *
 * Ritorna true se la lingua è stata applicata.
 */
export function applyServerLocale(language: string | null | undefined): boolean {
  const locale = resolveAppLocale(language)
  if (locale === null) return false
  // Memorizzata comunque: al reload non c'è un login da cui rileggerla, e senza valore
  // l'interfaccia ripartirebbe in italiano per un utente che il server vuole in inglese.
  localStorage.setItem(SERVER_STORAGE_KEY, locale)
  if (hasExplicitLocale()) return false
  applyLocale(locale)
  return true
}
